import { Button } from "@/components/ui/button";
import FilePreviewModal from "@/components/FilePreview/FilePreviewModal";
import { cn } from "@/lib/utils";
import { FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import Loading from "@/components/Loading";
import { useTRPC } from "@/trpc";
import { StateLicensesCard } from "@/features/profile/components/StateLicensesCard";
import { CertificationsCard } from "@/features/profile/components/CertificationsCard";
import { SpecialtiesCard } from "@/features/profile/components/SpecialtiesCard";
import type {
  CredentialField,
  CredentialFieldEntry,
} from "@/features/credential-fields/types/cedential-field";
import type { FileType } from "@/features/files/types/file-type";

const SHEET_CREDENTIAL_FIELDS = [
  "state_medical_licenses",
  "imlc_license",
  "dea",
  "cds",
  "bls",
  "acls",
  "specialty",
];
import {
  classify,
  formatDate,
  formatRelativeDays,
  getTodayInTimezone,
  type CredentialDocument,
  type CredentialStatus,
  type ProviderCredentialSummary,
} from "./classify";
import {
  CredentialStatusBadge,
  documentSeverityDotClass,
} from "./status-badges";

const noop = () => {};

const getField = <T,>(field: unknown) =>
  field as CredentialField<T> | undefined;

const nonNullEntries = <T,>(field: CredentialField<T> | undefined) =>
  (field?.values?.filter(
    (e): e is CredentialFieldEntry<T> & { value: T } =>
      e?.value !== null && e?.value !== undefined,
  ) ?? []) as Array<{ value: T; index: number }>;

type StateLicenseValue = {
  state: string | null;
  license_number: string | null;
  expiration_date: string | null;
};
type ImlcValue = {
  has_license: boolean;
  state_codes: string[];
  expiration_date?: string | null;
};
type SpecialtyValue = { specialty_code: string };

const ISSUE_RANK: Record<CredentialDocument["status"], number> = {
  expired: 0,
  missing: 1,
  "expiring-soon": 2,
  current: 3,
};

const sortDocuments = (documents: CredentialDocument[]) =>
  [...documents].sort((a, b) => {
    if (a.required !== b.required) {
      return a.required ? -1 : 1;
    }
    return ISSUE_RANK[a.status] - ISSUE_RANK[b.status];
  });

const fileLabel = (file: FileType) =>
  file.type_name || file.generated_title || file.file_name;

const fileExpirationText = (file: FileType, timezoneId?: string) => {
  if (!file.expires_at) {
    return "No expiration on file";
  }
  return `Expires ${formatDate(file.expires_at)} · ${formatRelativeDays(
    file.expires_at,
    timezoneId,
  )}`;
};

function FileCredentialRow({
  file,
  status,
  optional,
  timezoneId,
  onViewFile,
}: {
  file: FileType;
  status: CredentialStatus;
  optional: boolean;
  timezoneId?: string;
  onViewFile: (file: FileType) => void;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{fileLabel(file)}</div>
        <div className="truncate text-xs text-muted-foreground">
          {fileExpirationText(file, timezoneId)}
        </div>
      </div>
      <CredentialStatusBadge
        status={status}
        optional={optional && status === "missing"}
        className="shrink-0"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 shrink-0 gap-1.5 px-2 text-xs"
        onClick={() => onViewFile(file)}
      >
        View
      </Button>
    </div>
  );
}

function DocumentRow({
  doc,
  files,
  timezoneId,
  onViewFile,
}: {
  doc: CredentialDocument;
  files: FileType[];
  timezoneId?: string;
  onViewFile: (file: FileType) => void;
}) {
  const hasFiles = files.length > 0;
  const today = getTodayInTimezone(timezoneId);

  return (
    <div className="flex items-start gap-3 py-3">
      <span
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          documentSeverityDotClass({
            status: doc.status,
            optional: !doc.required,
          }),
        )}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{doc.type}</span>
          {!doc.required && (
            <span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Optional
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
          {hasFiles ? (
            <span>
              {files.length} {files.length === 1 ? "document" : "documents"} on
              file
            </span>
          ) : doc.status === "missing" && !doc.required ? (
            <span>Optional document not on file</span>
          ) : doc.status === "missing" ? (
            <span>No document on file</span>
          ) : doc.expiresAt ? (
            <>
              <span>Expires {formatDate(doc.expiresAt)}</span>
              <span>•</span>
              <span>{formatRelativeDays(doc.expiresAt, timezoneId)}</span>
            </>
          ) : (
            <span>No expiration</span>
          )}
          {doc.notes && (
            <>
              <span>•</span>
              <span className="italic">{doc.notes}</span>
            </>
          )}
        </div>
        {hasFiles && (
          <div className="mt-2 divide-y">
            {files.map((file) => {
              const status = classify(file.expires_at, today, true);
              return (
                <FileCredentialRow
                  key={file.id}
                  file={file}
                  status={status}
                  optional={!doc.required}
                  timezoneId={timezoneId}
                  onViewFile={onViewFile}
                />
              );
            })}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        {!hasFiles && (
          <CredentialStatusBadge
            status={doc.status}
            optional={!doc.required && doc.status === "missing"}
          />
        )}
      </div>
    </div>
  );
}

/** The legacy-model credential record, shown inline on the provider page. */
export function LegacyProviderCredentials({
  provider,
  organizationBusinessUnitId,
  timezoneId,
  workspaceActions,
  allowedFileVersionIds,
  credentialFallback,
  children,
}: {
  provider: ProviderCredentialSummary | null;
  organizationBusinessUnitId?: string;
  timezoneId?: string;
  workspaceActions?: ReactNode;
  allowedFileVersionIds?: string[];
  credentialFallback?: ReactNode;
  children?: ReactNode;
}) {
  const documents = sortDocuments(provider?.documents ?? []);
  const [previewFile, setPreviewFile] = useState<FileType | null>(null);

  const trpc = useTRPC();
  const buId = organizationBusinessUnitId || undefined;
  const userId = provider?.id ?? null;

  const credentialsQuery = useQuery({
    ...trpc.credentialFiles.getCredentialsFields.queryOptions({
      fieldCodes: SHEET_CREDENTIAL_FIELDS,
      switchUserId: userId ?? "",
      organizationBusinessUnitId: buId,
    }),
    enabled: !!userId && !credentialFallback,
  });

  const filesQuery = useQuery({
    ...trpc.credentialFiles.getFilesCheckPermission.queryOptions({
      switchUserId: userId ?? "",
      organizationBusinessUnitId: buId,
    }),
    enabled: !!userId && !credentialFallback,
  });

  const credentials = credentialsQuery.data;
  const filesByTypeId = useMemo(() => {
    const map = new Map<number, FileType[]>();
    const allowedVersions = allowedFileVersionIds
      ? new Set(allowedFileVersionIds)
      : null;
    for (const file of filesQuery.data?.data ?? []) {
      if (allowedVersions && !allowedVersions.has(file.id)) {
        continue;
      }
      if (file.type_id == null) {
        continue;
      }
      const files = map.get(file.type_id) ?? [];
      files.push(file);
      map.set(file.type_id, files);
    }
    return map;
  }, [filesQuery.data?.data, allowedFileVersionIds]);

  const findFilesForDocument = (doc: CredentialDocument) => {
    const files: FileType[] = [];
    const seenFileIds = new Set<string>();
    for (const typeId of doc.fileTypeIds) {
      for (const file of filesByTypeId.get(typeId) ?? []) {
        if (seenFileIds.has(file.id)) {
          continue;
        }
        seenFileIds.add(file.id);
        files.push(file);
      }
    }
    if (files.length > 0 || !doc.fileTypeId) {
      return files;
    }
    return filesByTypeId.get(doc.fileTypeId) ?? [];
  };

  const stateLicenses = nonNullEntries(
    getField<StateLicenseValue>(credentials?.state_medical_licenses),
  );
  const imlcLicense =
    getField<ImlcValue>(credentials?.imlc_license)?.values?.[0]?.value ?? null;
  const specialties = nonNullEntries(
    getField<SpecialtyValue>(credentials?.specialty),
  );
  const certifications = {
    dea: nonNullEntries(getField(credentials?.dea)),
    cds: nonNullEntries(getField(credentials?.cds)),
    bls: nonNullEntries(getField(credentials?.bls)),
    acls: nonNullEntries(getField(credentials?.acls)),
  };

  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      {credentialFallback ?? (
        <>
          <div className="p-4 pb-4 sm:p-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Credentials ({documents.length})
              </h3>
            </div>
            <div className="divide-y rounded-md border bg-background">
              {documents.map((doc) => (
                <div key={doc.id} className="px-4">
                  <DocumentRow
                    doc={doc}
                    files={findFilesForDocument(doc)}
                    timezoneId={timezoneId}
                    onViewFile={setPreviewFile}
                  />
                </div>
              ))}
            </div>
          </div>

          {filesQuery.isLoading && (
            <div className="px-6 pb-2 text-xs text-muted-foreground">
              Loading file previews…
            </div>
          )}

          {filesQuery.data?.hasPermission === false && (
            <div className="px-6 pb-2 text-xs text-muted-foreground">
              Credential files are not available for preview.
            </div>
          )}

          <div className="space-y-0 p-4 sm:p-6">
            {credentialsQuery.isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loading className="h-10 w-10" />
              </div>
            ) : credentialsQuery.error || !credentials ? (
              <div className="rounded-md border bg-background p-6 text-center text-sm text-muted-foreground">
                Couldn't load credential details.
              </div>
            ) : (
              <>
                <StateLicensesCard
                  editable={false}
                  isEditing={false}
                  onEditChange={noop}
                  stateLicenses={stateLicenses}
                  imlcLicense={
                    imlcLicense
                      ? {
                          has_license: imlcLicense.has_license,
                          state_codes: imlcLicense.state_codes,
                        }
                      : null
                  }
                  userId={userId ?? ""}
                />
                <CertificationsCard
                  editable={false}
                  isEditing={false}
                  onEditChange={noop}
                  certifications={certifications}
                  userId={userId ?? ""}
                />
                <SpecialtiesCard
                  editable={false}
                  isEditing={false}
                  onEditChange={noop}
                  specialties={specialties}
                  userId={userId ?? ""}
                />
              </>
            )}
          </div>
        </>
      )}
      {workspaceActions && (
        <div className="flex items-center justify-between gap-3 border-t p-4">
          {workspaceActions}
        </div>
      )}
      {children}
      {previewFile && userId && (
        <FilePreviewModal
          userId={userId}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          editable={false}
          organizationBusinessUnitId={buId}
        />
      )}
    </div>
  );
}
